import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  Pagination,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import {
  brandManagementService,
  AdminBrandItem,
} from '../../services/api/brandManagement.service';

interface BrandFormData {
  brand_id: string;
  brand_name: string;
  instagram_username: string;
  instagram_id: string;
  instagram_token: string;
  tiktok_username: string;
  tiktok_id: string;
}

const emptyForm: BrandFormData = {
  brand_id: '',
  brand_name: '',
  instagram_username: '',
  instagram_id: '',
  instagram_token: '',
  tiktok_username: '',
  tiktok_id: '',
};

const BrandManagementPage: React.FC = () => {
  const [brands, setBrands] = useState<AdminBrandItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedBrand, setSelectedBrand] = useState<AdminBrandItem | null>(null);
  const [formData, setFormData] = useState<BrandFormData>({ ...emptyForm });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<AdminBrandItem | null>(null);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuBrand, setMenuBrand] = useState<AdminBrandItem | null>(null);

  const isReadOnly = dialogMode === 'view';

  const loadBrands = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await brandManagementService.getBrands({
        page,
        limit: 10,
        search: searchQuery || undefined,
      });
      setBrands(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      setError('Failed to load brands');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, brand: AdminBrandItem) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuBrand(brand);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuBrand(null);
  };

  const openCreateDialog = () => {
    setDialogMode('create');
    setFormData({ ...emptyForm });
    setSelectedBrand(null);
    setDialogOpen(true);
  };

  const openEditDialog = (brand: AdminBrandItem) => {
    setDialogMode('edit');
    setSelectedBrand(brand);
    setFormData({
      brand_id: brand.brand_id || '',
      brand_name: brand.brand_name || '',
      instagram_username: brand.instagram_username || '',
      instagram_id: brand.instagram_id || '',
      instagram_token: brand.instagram_token || '',
      tiktok_username: brand.tiktok_username || '',
      tiktok_id: brand.tiktok_id || '',
    });
    setDialogOpen(true);
  };

  const openViewDialog = (brand: AdminBrandItem) => {
    setDialogMode('view');
    setSelectedBrand(brand);
    setFormData({
      brand_id: brand.brand_id || '',
      brand_name: brand.brand_name || '',
      instagram_username: brand.instagram_username || '',
      instagram_id: brand.instagram_id || '',
      instagram_token: brand.instagram_token || '',
      tiktok_username: brand.tiktok_username || '',
      tiktok_id: brand.tiktok_id || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.brand_id || !formData.brand_name) {
      setError('Brand ID and Brand Name are required');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (dialogMode === 'create') {
        await brandManagementService.createBrand(formData);
        setSuccessMessage('Brand created successfully');
      } else if (dialogMode === 'edit' && selectedBrand) {
        await brandManagementService.updateBrand(selectedBrand.id, formData);
        setSuccessMessage('Brand updated successfully');
      }
      setDialogOpen(false);
      loadBrands();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to save brand';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!brandToDelete) return;

    setLoading(true);
    setError(null);
    try {
      await brandManagementService.deleteBrand(brandToDelete.id);
      setSuccessMessage('Brand deleted successfully');
      setDeleteDialogOpen(false);
      setBrandToDelete(null);
      loadBrands();
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to delete brand';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Brand Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
        >
          Add Brand
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by brand ID, name, or Instagram..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setSearchQuery(''); setPage(1); }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Brand ID</TableCell>
                <TableCell>Brand Name</TableCell>
                <TableCell>Instagram</TableCell>
                <TableCell>TikTok</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && brands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : brands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No brands found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                brands.map((brand) => (
                  <TableRow key={brand.id} hover>
                    <TableCell>
                      <Chip label={brand.brand_id} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {brand.brand_name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {brand.instagram_username ? (
                        <Chip
                          icon={<InstagramIcon />}
                          label={brand.instagram_username}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: '#E1306C', color: '#E1306C' }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {brand.tiktok_username ? (
                        <Chip
                          label={brand.tiktok_username}
                          size="small"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(brand.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, brand)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" py={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuBrand) openViewDialog(menuBrand);
            handleMenuClose();
          }}
        >
          <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuBrand) openEditDialog(menuBrand);
            handleMenuClose();
          }}
        >
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (menuBrand) {
              setBrandToDelete(menuBrand);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit/View Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {dialogMode === 'create' ? 'Add Brand' : dialogMode === 'edit' ? 'Edit Brand' : 'View Brand'}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Brand ID"
                  value={formData.brand_id}
                  onChange={(e) => setFormData({ ...formData, brand_id: e.target.value.toUpperCase() })}
                  disabled={isReadOnly || dialogMode === 'edit'}
                  required
                  inputProps={{ maxLength: 10 }}
                  helperText="e.g. KK, LO, BG"
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Brand Name"
                  value={formData.brand_name}
                  onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                  disabled={isReadOnly}
                  required
                />
              </Grid>
            </Grid>

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">Instagram</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instagram Username"
                  value={formData.instagram_username}
                  onChange={(e) => setFormData({ ...formData, instagram_username: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Instagram ID"
                  value={formData.instagram_id}
                  onChange={(e) => setFormData({ ...formData, instagram_id: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              size="small"
              label="Instagram Token"
              value={formData.instagram_token}
              onChange={(e) => setFormData({ ...formData, instagram_token: e.target.value })}
              disabled={isReadOnly}
              type={isReadOnly ? 'text' : 'password'}
            />

            <Divider />
            <Typography variant="subtitle2" color="text.secondary">TikTok</Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="TikTok Username"
                  value={formData.tiktok_username}
                  onChange={(e) => setFormData({ ...formData, tiktok_username: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="TikTok ID"
                  value={formData.tiktok_id}
                  onChange={(e) => setFormData({ ...formData, tiktok_id: e.target.value })}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>

            {dialogMode === 'view' && selectedBrand && (
              <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Info
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Created:</strong> {new Date(selectedBrand.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Updated:</strong> {selectedBrand.updated_at ? new Date(selectedBrand.updated_at).toLocaleString() : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>
          {!isReadOnly && (
            <Button variant="contained" onClick={handleSave} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : dialogMode === 'create' ? 'Create' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete brand <strong>{brandToDelete?.brand_name}</strong> ({brandToDelete?.brand_id})?
            This may affect places, templates, and other data linked to this brand.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrandManagementPage;
